import { NormalizedPreferences, SerializedPreferences } from "./types";
import {
  useAuthentication,
  useEditPreferences,
  useRetrievePreferences,
} from "@planzone/view-models";
import { Identifiable, Resource, Sort } from "@planzone/dto";

function serializePreferences(
  normalized: NormalizedPreferences,
  namespace: string,
): {
  [key: string]: string;
} {
  return Object.entries({
    isLayoutSidebarMinimized: normalized.isLayoutSidebarMinimized.toString(),
    isTimeSheetMinimized: normalized.isTimeSheetMinimized.toString(),
    showDeactivatedProjectsOnTimeSheets:
      normalized.showDeactivatedProjectsOnTimeSheets.toString(),
    showDeactivatedResourcesOnTimeSheets:
      normalized.showDeactivatedResourcesOnTimeSheets.toString(),
    areWorkDoneShownOnTimeSheets:
      normalized.areWorkDoneShownOnTimeSheets.toString(),
    ganttViewMode: normalized.ganttViewMode,
    resourcesFilter: JSON.stringify(
      normalized.resourcesFilter || {
        resourceTypes: [],
        roles: [],
        areSortedByGroups: false,
        inactiveOnly: false,
        group: null,
        resourceTags: [],
      },
    ),
    zonesProjectsFilter: JSON.stringify(
      normalized.zonesProjectsFilter || {
        projects: [],
        projectStatuses: [],
        projectsTags: [],
      },
    ),
    zoneProjectsFilter: JSON.stringify(
      normalized.zoneProjectsFilter || {
        projects: [],
        projectPortfolios: [],
        projectStatuses: [],
        projectsTags: [],
      },
    ),
    projectPortfoliosFilter: JSON.stringify(
      normalized.projectPortfoliosFilter || {
        projects: [],
        projectStatuses: [],
        projectsTags: [],
      },
    ),
    zoneProjectReportsFilter: JSON.stringify(
      normalized.zoneProjectReportsFilter || {
        projects: [],
        projectPortfolios: [],
        projectStatuses: [],
        projectsTags: [],
        groups: [],
        resources: [],
      },
    ),
    zonesProjectReportsFilter: JSON.stringify(
      normalized.zonesProjectReportsFilter || {
        projects: [],
        projectPortfolios: [],
        projectStatuses: [],
        projectsTags: [],
        groups: [],
        resources: [],
      },
    ),
    calendarFilter: JSON.stringify(
      normalized.calendarFilter || {
        projects: [],
        projectPortfolios: [],
        projectStatuses: [],
        projectsTags: [],
        resources: [],
        elementTypes: ["task", "milestone", "event", "unavailability"],
      },
    ),
    zoneCalendarFilter: JSON.stringify(
      normalized.zoneCalendarFilter || {
        projects: [],
        resources: [],
        elementTypes: ["task", "milestone", "event", "unavailability"],
      },
    ),
    projectCalendarFilter: JSON.stringify(
      normalized.projectCalendarFilter || {
        resources: [],
        elementTypes: ["task", "milestone", "event", "unavailability"],
      },
    ),
    projectResourcesFilter: JSON.stringify(
      normalized.projectResourcesFilter || {
        resourceTypes: [],
        roles: [],
        areSortedByGroups: false,
        inactiveOnly: false,
        group: null,
        tags: [],
      },
    ),
    wbsFilter: JSON.stringify(
      normalized.wbsFilter || {
        scope: "project",
        elementTypes: ["list", "task", "milestone", "event", "completedWork"],
        projects: [],
      },
    ),
    wbsSort: normalized.wbsSort || "ascendingRank",
    obsFilter: JSON.stringify(normalized.obsFilter || {}),
    personalTimeSheetFilter: JSON.stringify(
      normalized.personalTimeSheetFilter || {
        projects: [],
        projectStatuses: [],
      },
    ),
    projectTimeSheetFilter: JSON.stringify(
      normalized.projectTimeSheetFilter || {
        projects: [],
        projectStatuses: [],
      },
    ),
    projectListingColumnSizes: JSON.stringify(
      normalized.projectListingColumnSizes || {},
    ),
    showBlockedTasks: normalized.showBlockedTasks.toString(),
  }).reduce(
    (map, [key, value]) => ({ ...map, [`${namespace}:${key}`]: value }),
    {},
  );
}

function normalizePreferences(
  serialized: SerializedPreferences,
  namespace: string,
  resource: Identifiable<Resource>,
): NormalizedPreferences {
  const get = (key: string) => serialized[`${namespace}:${key}`];

  return {
    isLayoutSidebarMinimized: get("isLayoutSidebarMinimized") === "true",
    isTimeSheetMinimized: get("isTimeSheetMinimized") === "true",
    showDeactivatedProjectsOnTimeSheets:
      typeof get("showDeactivatedProjectsOnTimeSheets") !== "undefined"
        ? get("showDeactivatedProjectsOnTimeSheets") === "true"
        : true,
    showDeactivatedResourcesOnTimeSheets:
      typeof get("showDeactivatedResourcesOnTimeSheets") != "undefined"
        ? get("showDeactivatedResourcesOnTimeSheets") === "true"
        : true,
    areWorkDoneShownOnTimeSheets:
      typeof get("areWorkDoneShownOnTimeSheets") !== "undefined"
        ? get("areWorkDoneShownOnTimeSheets") === "true"
        : true,
    ganttViewMode: get("ganttViewMode"),
    resourcesFilter: get("resourcesFilter")
      ? JSON.parse(get("resourcesFilter"))
      : {
          resourceTypes: [],
          roles: [],
          areSortedByGroups: false,
          inactiveOnly: false,
          group: null,
          resourceTags: [],
        },
    zonesProjectsFilter: get("zonesProjectsFilter")
      ? JSON.parse(get("zonesProjectsFilter"))
      : {
          projects: [],
          projectStatuses: [],
          projectsTags: [],
        },
    zoneProjectsFilter: get("zoneProjectsFilter")
      ? JSON.parse(get("zoneProjectsFilter"))
      : {
          projects: [],
          projectPortfolios: [],
          projectStatuses: [],
          projectsTags: [],
        },
    projectPortfoliosFilter: get("projectPortfoliosFilter")
      ? JSON.parse(get("projectPortfoliosFilter"))
      : {
          projects: [],
          projectStatuses: [],
          projectsTags: [],
        },
    zoneProjectReportsFilter: get("zoneProjectReportsFilter")
      ? JSON.parse(get("zoneProjectReportsFilter"))
      : {
          projects: [],
          projectPortfolios: [],
          projectStatuses: [],
          projectsTags: [],
          groups: [],
          resources: [],
        },
    zonesProjectReportsFilter: get("zonesProjectReportsFilter")
      ? JSON.parse(get("zonesProjectReportsFilter"))
      : {
          projects: [],
          projectPortfolios: [],
          projectStatuses: [],
          projectsTags: [],
          groups: [],
          resources: [],
        },
    calendarFilter: get("calendarFilter")
      ? JSON.parse(get("calendarFilter"))
      : {
          projects: [],
          projectPortfolios: [],
          projectStatuses: [],
          projectsTags: [],
          resources: [resource],
          elementTypes: ["task", "milestone", "event", "unavailability"],
        },
    zoneCalendarFilter: get("zoneCalendarFilter")
      ? JSON.parse(get("zoneCalendarFilter"))
      : {
          projects: [],
          resources: [],
          elementTypes: ["task", "milestone", "event", "unavailability"],
        },
    projectCalendarFilter: get("projectCalendarFilter")
      ? JSON.parse(get("projectCalendarFilter"))
      : {
          resources: [],
          elementTypes: ["task", "milestone", "event", "unavailability"],
        },
    projectResourcesFilter: get("projectResourcesFilter")
      ? JSON.parse(get("projectResourcesFilter"))
      : {
          resourceTypes: [],
          roles: [],
          areSortedByGroups: false,
          inactiveOnly: false,
          group: null,
          tags: [],
        },
    wbsFilter: get("wbsFilter")
      ? JSON.parse(get("wbsFilter"))
      : {
          scope: "project",
          elementTypes: ["list", "task", "milestone", "event", "completedWork"],
          projects: [],
        },
    wbsSort: !!get("wbsSort") ? (get("wbsSort") as Sort) : "ascendingRank",
    obsFilter: get("obsFilter") ? JSON.parse(get("obsFilter")) : {},
    personalTimeSheetFilter: get("personalTimeSheetFilter")
      ? JSON.parse(get("personalTimeSheetFilter"))
      : {
          projects: [],
          projectStatuses: [],
          includeDeactivated: true,
          includeRemovedFromProject: true,
          includeCompletedTasks: true,
          includeProjectsWithoutSpentTime: true,
          includeProjectsWithoutSpentTimeInWeek: true,
          includeProjectsWithoutNonIntegratedSpentTimeInWeek: true,
        },
    projectTimeSheetFilter: get("projectTimeSheetFilter")
      ? JSON.parse(get("projectTimeSheetFilter"))
      : {
          resources: [],
          includeDeactivated: true,
          includeRemovedFromProject: true,
          includeCompletedTasks: true,
        },
    projectListingColumnSizes: get("projectListingColumnSizes")
      ? JSON.parse(get("projectListingColumnSizes"))
      : {},
    showBlockedTasks:
      typeof get("showBlockedTasks") !== "undefined"
        ? get("showBlockedTasks") === "true"
        : true,
  };
}

function usePreferences() {
  const { preferences } = useRetrievePreferences();
  const { account } = useAuthentication();
  const { editPreferences } = useEditPreferences();

  return {
    state: normalizePreferences(
      preferences,
      account.resource.uuid,
      account.resource,
    ),
    edit(preferences: NormalizedPreferences) {
      editPreferences(serializePreferences(preferences, account.resource.uuid));
    },
  };
}

export { usePreferences };
